import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button, OverlayTrigger, Table, Tooltip } from "react-bootstrap";
import Spinner from "@services/ui-components/src/Spinner/Spinner";
import AddWarehouseModal from "../modals/AddWarehouseModal";
import EditWarehouseModal from "../modals/EditWarehouseModal";
import DeleteWarehouseModals from "../modals/DeleteWarehouseModals";
import { ApolloError, useReactiveVar } from "@apollo/client";
import { Pagination, RenderEmptyList, WithRoleAccess } from "@services/ui-components";
import { useGetStorageTypesQuery, useGetWarehousesQuery } from "@me-team/host/main/warehouses/graphql/warehouses.hooks";
import { CompanyAccess, PermissionScope, StorageProxy } from "@me-team/host/main/graphql/types";
import ToastComponent from "../../ui-components/ToastComponent/ToastComponent";
import { useToast } from "../../../hooks/useToast";
import { currentCompanyIdVar } from "@me-team/host/main/globalVar/state";
import useScrollToTopOnChange from "../../../hooks/useScrollToTopOnChange";
import { ErrorModalComponent } from "@me-pos/error-modal";
import i18next from "i18next";
import { SaleProducts } from "../../index";
import ErrorService from "../../../services/ErrorService";
import {
    getCurrentStepFromLocalStorage,
    getWarehouseTutorialType,
    isWarehouseTutorialActive,
    moveStepInLocalStorage,
    StepDirection,
} from "../../Joyride/WarehouseTutorial/joyrideStepHelpers";
import WarehouseJoyrideWrapper from "../../Joyride/WarehouseTutorial/WarehouseJoyrideWrapper";
import StepScreenshotModal from "../../Joyride/WarehouseTutorial/Modals/StepScreenshotModal";
import { saleFromRecordScreenshots } from "../../Joyride/WarehouseTutorial/constants";
import { removeHandler, setHandler } from "../../Joyride/WarehouseTutorial/utils/handlerRegistry";
import { useRoleAccess } from "@me-team/host/src/utils/useRoleAccess";

interface WarehousesTableProps {
    show: boolean;
    handleModal: () => void;
    branchId: number;
    currentPage: number;
    setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
    isOpenProductSaleModal: boolean;
    onToggleProductSaleModal: () => void;
}

const TUTORIAL_STEPS_WITH_SCREENS = new Set([10]);

const WarehousesTable: React.FC<WarehousesTableProps> = ({
    show,
    handleModal,
    branchId,
    currentPage,
    setCurrentPage,
    isOpenProductSaleModal,
    onToggleProductSaleModal,
}) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const currentCompanyId = useReactiveVar(currentCompanyIdVar);
    const canCreateWarehouse = useRoleAccess(CompanyAccess.WarehouseCreate);
    const canEditWarehouse = useRoleAccess(CompanyAccess.WarehouseEdit);
    const canDeleteRestoreWarehouse = useRoleAccess(CompanyAccess.WarehouseDeleteRestore);
    const canOpenWarehouseProducts = useRoleAccess(CompanyAccess.WarehouseProductListView);
    const canCreateSale = useRoleAccess(CompanyAccess.WarehouseOperationSaleCreate, {
        scope: PermissionScope.Self,
    });
    const [isErrorModalOpen, setIsErrorModalOpen] = useState<number>(null);
    const [itemsPerPage] = useState(20);
    const [showWarehouseEditModal, setShowWarehouseEditModal] = useState(false);
    const [warehouseEdit, setWarehouseEdit] = useState<StorageProxy>(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const { showToast, toastText, setShowToast, toggleShowToast } = useToast();
    const tutorialActive = isWarehouseTutorialActive();
    const tutorialType = getWarehouseTutorialType();
    const startStep = getCurrentStepFromLocalStorage();
    const [currentStep, setCurrentStep] = useState(() => startStep);
    const [showStepScreenshotModal, setShowStepScreenshotModal] = useState<boolean>(false);
    const handleCloseStepScreenshotModal = () => setShowStepScreenshotModal(false);

    useScrollToTopOnChange(currentPage);

    useEffect(() => {
        setCurrentStep(startStep);
        setShowStepScreenshotModal(TUTORIAL_STEPS_WITH_SCREENS.has(startStep));
    }, [startStep]);

    const handlerMovementsOnSteps = useCallback(() => {
        const stepFromStorage = getCurrentStepFromLocalStorage();
        if (stepFromStorage === 11) setShowStepScreenshotModal(false);
    }, []);

    useEffect(() => {
        const id = "screenshot-modal-handler";
        setHandler(id, handlerMovementsOnSteps);
        return () => removeHandler(id);
    }, [handlerMovementsOnSteps]);

    const { data, loading, refetch } = useGetWarehousesQuery({
        variables: {
            page: currentPage,
            itemsPerPage: itemsPerPage,
            currentBranchId: +branchId,
            companyId: +currentCompanyId,
        },
        skip: !currentCompanyId || !branchId,
        context: {
            errorType: "global",
        },
        onError: (error: ApolloError) => {
            setIsErrorModalOpen(ErrorService.errorHandling(error));
        },
    });
    const handlePageChange = (pageNumber: number) => setCurrentPage(pageNumber);
    const { data: storageTypes } = useGetStorageTypesQuery({
        context: {
            errorType: "local",
        },
    });
    const pages = data?.user?.company?.branchesPaginated?.branches[0]?.storagesPaginated;
    const totalItems = data?.user?.company?.branchesPaginated?.branches[0]?.storagesPaginated?.totalCount || 0;
    const firstItemIndex = (currentPage - 1) * itemsPerPage + 1;
    const lastItemIndex = Math.min(currentPage * itemsPerPage, totalItems);

    const warehouses = data?.user?.company?.branchesPaginated?.branches[0]?.storagesPaginated?.storages;
    const hasWarehouseActions = canEditWarehouse || canDeleteRestoreWarehouse || canOpenWarehouseProducts;

    const storages = storageTypes?.relations?.storageTypes.map((storage) => ({
        value: storage.id,
        label: storage.name,
    }));
    const countProductsQuantity = (data) => data.reduce((count, item) => (item.quantity > 0 ? count + 1 : count), 0);

    const editWarehouse = (warehouse: StorageProxy | null) => {
        setShowWarehouseEditModal((prevState) => !prevState);
        setWarehouseEdit(warehouse);
    };
    const handleDeleteModal = (warehouse: StorageProxy | null) => {
        setShowDeleteModal((prevState) => !prevState);
        setWarehouseEdit(warehouse);
    };
    if (loading || !currentCompanyId) return <Spinner />;

    return (
        <>
            {warehouses?.length > 0 ? (
                <Table responsive>
                    <thead className="fs-7">
                        <tr>
                            <th className="px-4">{t("Warehouse name")}</th>
                            <th>{t("Description")}</th>
                            <th>{t("Warehouse type")}</th>
                            <th>{t("Products quantity")}</th>
                            {hasWarehouseActions ? <th></th> : null}
                        </tr>
                    </thead>
                    <tbody>
                        {warehouses?.map((i) => (
                            <tr key={i?.id}>
                                <td className="px-4 align-middle">
                                    {i?.deleted ? (
                                        <span className="text-secondary">{i?.name}</span>
                                    ) : (
                                        <WithRoleAccess
                                            permission={CompanyAccess.WarehouseProductListView}
                                            fallback={<span className="text-dark">{i?.name}</span>}
                                        >
                                            <Link
                                                to={`/warehouses/warehouses-goods/branch/${branchId}/${i?.id}`}
                                                className="text-decoration-none"
                                                onClick={() =>
                                                    tutorialActive && moveStepInLocalStorage(StepDirection.NEXT)
                                                }
                                            >
                                                {i?.name}
                                            </Link>
                                        </WithRoleAccess>
                                    )}
                                </td>
                                <td className={`${i?.deleted ? "text-secondary" : ""} align-middle`}>
                                    {i?.description}
                                </td>
                                <td className={`${i?.deleted ? "text-secondary" : ""} align-middle`}>
                                    {i?.type?.name}
                                </td>
                                <td className={`${i?.deleted ? "text-secondary" : ""} align-middle`}>
                                    {i?.storageProductRelations.length} (
                                    {countProductsQuantity(i?.storageProductRelations)})
                                </td>
                                {hasWarehouseActions ? (
                                    <td className="align-middle">
                                        <div className="d-flex justify-content-end">
                                            {!i?.deleted ? (
                                                <>
                                                    {canEditWarehouse ? (
                                                        <OverlayTrigger overlay={<Tooltip>{t("Edit")}</Tooltip>}>
                                                            <Button
                                                                variant="outline-secondary"
                                                                className="outline-secondary text-dark m-1 p-0"
                                                                onClick={() => editWarehouse(i)}
                                                                style={{ height: "39px", width: "39px" }}
                                                                disabled={tutorialActive}
                                                            >
                                                                <i className="bi bi-pencil"></i>
                                                            </Button>
                                                        </OverlayTrigger>
                                                    ) : null}
                                                    {canOpenWarehouseProducts ? (
                                                        <OverlayTrigger
                                                            overlay={<Tooltip>{t("Warehouse product")}</Tooltip>}
                                                        >
                                                            <Button
                                                                variant="outline-secondary"
                                                                className="outline-secondary text-dark m-1 p-0"
                                                                style={{ height: "39px", width: "39px" }}
                                                                onClick={() => {
                                                                    tutorialActive &&
                                                                        moveStepInLocalStorage(StepDirection.NEXT);
                                                                    navigate(
                                                                        `/warehouses/warehouses-goods/branch/${branchId}/${i?.id}`
                                                                    );
                                                                }}
                                                            >
                                                                <i className="bi bi-basket"></i>
                                                            </Button>
                                                        </OverlayTrigger>
                                                    ) : null}
                                                    {canDeleteRestoreWarehouse ? (
                                                        <OverlayTrigger
                                                            overlay={<Tooltip>{t("Delete warehouse")}</Tooltip>}
                                                        >
                                                            <Button
                                                                variant="outline-secondary"
                                                                className="outline-secondary text-dark m-1 p-0"
                                                                onClick={() => handleDeleteModal(i)}
                                                                style={{ height: "39px", width: "39px" }}
                                                                disabled={tutorialActive}
                                                            >
                                                                <i className="bi bi-trash"></i>
                                                            </Button>
                                                        </OverlayTrigger>
                                                    ) : null}
                                                </>
                                            ) : (
                                                <>
                                                    <span className="fs-7 badge bg-additional-grey text-grey px-3 py-2 m-1 rounded  mb-xl-0 d-flex justify-content-center align-items-center">
                                                        {t("Archive")}
                                                    </span>
                                                    {canDeleteRestoreWarehouse ? (
                                                        <OverlayTrigger overlay={<Tooltip>{t("Restore")} </Tooltip>}>
                                                            <Button
                                                                variant="outline-secondary"
                                                                className="outline-secondary text-dark m-1 p-0"
                                                                onClick={() => editWarehouse(i)}
                                                                style={{ height: "39px", width: "39px" }}
                                                                disabled={tutorialActive}
                                                            >
                                                                <i className="bi bi-arrow-counterclockwise"></i>
                                                            </Button>
                                                        </OverlayTrigger>
                                                    ) : null}
                                                </>
                                            )}
                                        </div>
                                    </td>
                                ) : null}
                            </tr>
                        ))}
                    </tbody>
                </Table>
            ) : (
                <RenderEmptyList
                    isFilterNotEmpty={false}
                    emptyFilterTitle=""
                    emptyListTitle="warehouses not available"
                    onClickButton={handleModal}
                    buttonName={
                        <span>
                            <i className="bi bi-plus-lg fw-bold me-1 w-100"></i>
                            {t("Add Warehouse")}
                        </span>
                    }
                    isEmployeeRole={!canCreateWarehouse}
                />
            )}
            {warehouses?.length > 0 ? (
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <Pagination
                        t={t}
                        pages={Number(pages?.pageCount)}
                        currentPage={currentPage}
                        onPageChange={handlePageChange}
                        firstItemIndex={firstItemIndex}
                        lastItemIndex={lastItemIndex}
                        totalItems={pages?.totalCount}
                    />
                </div>
            ) : null}
            <AddWarehouseModal
                show={show}
                handleModal={handleModal}
                branchId={branchId}
                storages={storages}
                refetch={refetch}
                toggleShowToast={toggleShowToast}
            />
            <EditWarehouseModal
                show={showWarehouseEditModal}
                handleModal={() => editWarehouse(null)}
                warehouse={warehouseEdit}
                storages={storages}
                refetch={refetch}
                toggleShowToast={toggleShowToast}
            />
            <DeleteWarehouseModals
                show={showDeleteModal}
                handleModal={() => setShowDeleteModal(false)}
                warehouse={warehouseEdit}
                refetch={refetch}
                toggleShowToast={toggleShowToast}
            />
            {canCreateSale && (tutorialActive || isOpenProductSaleModal) && (
                <SaleProducts
                    action="create"
                    showModal={isOpenProductSaleModal}
                    handleCloseModal={onToggleProductSaleModal}
                    toggleShowToast={toggleShowToast}
                    dialogClassName={tutorialActive && "sale-product-modal"}
                    centeredModal={tutorialActive && false}
                    dataTour="sale-product-form"
                />
            )}

            {showStepScreenshotModal && (
                <StepScreenshotModal
                    key={currentStep}
                    show={showStepScreenshotModal}
                    onClose={handleCloseStepScreenshotModal}
                    step={currentStep}
                    screenshots={saleFromRecordScreenshots}
                    dialogClassName={"screenshot"}
                    centeredModal={false}
                    dataTour="screenshot-modal"
                />
            )}

            {tutorialActive && tutorialType && (
                <WarehouseJoyrideWrapper
                    tutorialType={tutorialType}
                    startStep={startStep}
                    goNext={handlerMovementsOnSteps}
                />
            )}

            {showToast && <ToastComponent show={showToast} setShow={setShowToast} text={toastText} />}
            {isErrorModalOpen ? (
                <ErrorModalComponent
                    i18n={i18next}
                    onClose={() => {
                        setIsErrorModalOpen(null);
                    }}
                    isOpen={!!isErrorModalOpen}
                    currentError={isErrorModalOpen}
                />
            ) : null}
        </>
    );
};

export default WarehousesTable;
